<template>
  <div class="form-wrapper">
    <h3><slot name="txtBefore"></slot></h3>
    <b-form
      :id="'form' + _uid"
      :novalidate="true"
      class="contact-input-form"
      ref="form">
      <div class="form-row">
        <GenderRadioInput
            :gender.sync="formValues.customer.gender"
            :invalid.sync="invalid.customer.gender"
            ref="nestedInputGender"
          >
            <template v-for="slot in possibleChildSlotsFor('Gender')" v-slot:[`${slot.child}`]>
              <slot :name="`${slot.parent}`"></slot>
            </template>
          </GenderRadioInput>
      </div>
      <div class="form-row">
        <LabeledTextInput
          name="firstName"
          :model.sync="formValues.customer.firstName"
          :invalid.sync="invalid.customer.firstName"
          :validation="validations.customer.firstName"
          :formatter="formatterCharFilterOnlyAplha"
          :mandatory="true"
          ref="nestedInputFirstName"
        >
          <template v-for="slot in possibleChildSlotsFor('FirstName')" v-slot:[`${slot.child}`]>
            <slot :name="`${slot.parent}`"></slot>
          </template>
        </LabeledTextInput>
        <LabeledTextInput
          name="lastName"
          :model.sync="formValues.customer.lastName"
          :mandatory="true"
          :invalid.sync="invalid.customer.lastName"
          :validation="validations.customer.lastName"
          :formatter="formatterCharFilterOnlyAplha"
          ref="nestedInputLastName"
        >
          <template v-for="slot in possibleChildSlotsFor('LastName')" v-slot:[`${slot.child}`]>
            <slot :name="`${slot.parent}`"></slot>
          </template>
        </LabeledTextInput>
      </div>
      <div class="form-row">
        <LabeledTextInput
          name="email"
          :model.sync="formValues.customer.email"
          :mandatory="true"
          type="email"
          :invalid.sync="invalid.customer.email"
          :validation="validations.customer.email"
          ref="nestedInputEmail"
        >
          <template v-for="slot in possibleChildSlotsFor('Email')" v-slot:[`${slot.child}`]>
            <slot :name="`${slot.parent}`"></slot>
          </template>
        </LabeledTextInput>
        <LabeledPhoneNumberInput
          :model.sync="formValues.customer.phoneNumber"
          :invalid.sync="invalid.customer.phoneNumber"
          :mandatory="true"
          ref="nestedInputPhoneNumber"
        >
          <template v-for="slot in possibleChildSlotsFor('Phone')" v-slot:[`${slot.parent}`]>
            <slot :name="`${slot.parent}`"></slot>
          </template>
          <template v-for="slotError in possibleChildSlotsFor('Error')" v-slot:[`${slotError.parent}`]>
            <slot :name="`${slotError.parent}`"></slot>
          </template>
        </LabeledPhoneNumberInput>
      </div>
      <div class="form-row">
          <LabeledTextInput
            name="customerId"
            :model.sync="formValues.customer.customerId"
            :mandatory="false"
            type="text"
            :invalid.sync="invalid.customer.customerId"
            :validation="validations.customer.customerId"
            :formatter="customerIdFormatter()"
            ref="nestedInputCustomerId"
          >
            <template v-for="slot in possibleChildSlotsFor('CustomerId')" v-slot:[`${slot.child}`]>
              <slot :name="`${slot.parent}`"></slot>
            </template>
          </LabeledTextInput>
        </div>
        <div class="form-row">
          <LabeledBrandedDropdown
            name="contactreason"
            :options="slotOptions"
            :selected.sync="formValues.contact.contactReason"
            :invalid.sync="invalid.contact.contactReason"
            :validation="validations.contact.contactReason"
            ref="nestedInputContactReason"
            :mandatory="true">
            <template v-for="slot in possibleChildSlotsFor('Reason')" v-slot:[`${slot.child}`]>
              <slot :name="`${slot.parent}`"></slot>
            </template>
          </LabeledBrandedDropdown>
        </div>
        <div class="form-row">
        <LabeledTextInput
          name="contactmessage"
          :model.sync="formValues.contact.message"
          :mandatory="true"
          :invalid.sync="invalid.contact.message"
          :validation="validations.contact.message"
          ref="nestedInputMessage"
          type="textarea"
        >
          <template v-for="slot in possibleChildSlotsFor('Message')" v-slot:[`${slot.child}`]>
            <slot :name="`${slot.parent}`"></slot>
          </template>
        </LabeledTextInput>
      </div>

      <Stepper
        :show-prev="false"
        @next="submit()"
        :disabled-next="loading"
        :disabled-prev="loading"
      >
        <template v-for="slot in possibleChildSlotsFor('Stepper')" v-slot:[`${slot.child}`]>
          <slot :name="`${slot.parent}`"></slot>
        </template>
      </Stepper>
    </b-form>
  </div>
</template>

<script>
import { validationMixin } from 'vuelidate'
import isValid from '@/mixins/validators/isValid'
import validationFlow from '@/mixins/validators/validationFlow'
import formatterCharFilterOnlyAplha from '@/mixins/formatters/inputFormatter'
import formatDecimal from '@/mixins/formatters/formatDecimal'
import transaction from '@/mixins/transaction'

import beErrors from '@/mixins/beErrors'
import { maxLength, required } from 'vuelidate/lib/validators'
import emailValidator from '@/mixins/validators/emailValidator'
import LabeledBrandedDropdown from '@/components/forms/LabeledBrandedDropdown.vue'
import LabeledTextInput from '@/components/forms/LabeledTextInput.vue'
import LabeledPhoneNumberInput from '@/components/forms/LabeledPhoneNumberInput.vue'
import Stepper from '@/components/generic/Stepper.vue'
import GenderRadioInput from '@/components/forms/GenderRadioInput.vue'
import gql from 'graphql-tag'
import { splitError } from '@/helper/splitError'
import useFeature from '@/mixins/useFeature'

export default {
  name: 'ContactForm',
  components: { Stepper, LabeledTextInput, LabeledBrandedDropdown, LabeledPhoneNumberInput, GenderRadioInput },
  data  () {
    return {
      loading: false,
      formValues: {
        customer: {
          firstName: '',
          lastName: '',
          gender: '',
          email: '',
          phoneNumber: '',
          customerId: ''
        },
        contact: {
          contactReason: '',
          message: ''
        }
      },
      invalid: {
        customer: {
          gender: null,
          firstName: null,
          lastName: null,
          email: null,
          phoneNumber: null,
          customerId: null
        },
        contact: {
          contactReason: null,
          message: null
        }
      },
      validations: {
        customer: {
          firstName: {
            required,
            maxLength: maxLength(50)
          },
          lastName: {
            required,
            maxLength: maxLength(50)
          },
          email: {
            required,
            email: (v) => {
              return this.validateEmail(v.toLowerCase())
            }
          },
          customerId: {
            customerId: (v) => (v === null || /^[0-9]*$/.test(v))
          }
        },
        contact: {
          contactReason: {
            required
          },
          message: {
            required
          }
        }
      }
    }
  },
  computed: {
    slotOptions: function () {
      return this.getSlotListToArray('optionsReason')
    },
    customerInvalid: function () {
      return Object.entries(this.invalid.customer)
        .map(([key, value]) => value)
        .reduce((prev, current) => prev || current, false)
    },
    contactInvalid: function () {
      return Object.entries(this.invalid.contact)
        .map(([key, value]) => value)
        .reduce((prev, current) => prev || current, false)
    },
    formInvalid: function () {
      return this.contactInvalid || this.customerInvalid
    }
  },
  methods: {
    submit () {
      const comp = this
      comp.loading = true
      this.forceValidationOnNestedInputs()
      if (this.formInvalid) {
        comp.loading = false
        this.vFlowScrollToFirstElemment()
        this.vFlowGetFirstErrorElementName().then(function (result) {
          const data = {
            type: 'validation',
            message: 'required field missing',
            location: result + ' - unknown'
          }
          if (typeof window.trackingNS?.track?.tracking === 'function') {
            window.trackingNS.track.tracking('error', data)
          }
        })
        return
      }
      this.startLoader()
      comp.sendForm()
        .catch((e) => console.error(e))
        .finally(() => this.stopLoader())
    },
    forceValidationOnNestedInputs () {
      this.vflowForceFeedback()
      Object.entries(this.$refs).forEach(([key, comp]) => {
        if (/^nestedInput/.test(key)) {
          comp.vflowForceFeedback()
        }
      })
    },
    sendForm () {
      const comp = this
      return this.$apollo.mutate(({
        mutation: gql`mutation contactForm ($customerInput: CustomerContactFormInput!) {
            sendCustomerContactForm(customerContactForm: $customerInput) {
                success
            }
        }`,
        variables: {
          customerInput: {
            brand: 'simon',
            customer: {
              ...comp.formValues.customer,
              gender: (comp.formValues.customer.gender === 'NOT_SPECIFIED') ? '' : comp.formValues.customer.gender
            },
            form: {
              contactReason: comp.formValues.contact.contactReason.key,
              message: comp.formValues.contact.message
            }
          }
        }
      })).then((data) => {
        comp.successToast(comp.getSlotText('txtToastSuccess', 'Nachricht erfolgreich gesendet'))
        this.clearForm()
        comp.loading = false
        const trackData = {
          page_levels: [
            'contact',
            'submit'
          ],
          login_status: 'not logged in',
          page_type: 'content',
          platform_type: 'browser view',
          form: {
            name: 'contact',
            step: '2',
            step_name: 'submit',
            type: 'lead',
            status: 'success'
          }
        }
        if (typeof window.trackingNS?.track?.tracking === 'function') {
          window.trackingNS.track.tracking('pageview', trackData)
        }
      }).catch((error) => {
        comp.errorToast(this.errorMessage(error))
        comp.loading = false
        const trackData = {
          page_levels: [
            'contact',
            'submit'
          ],
          login_status: 'not logged in',
          page_type: 'content',
          platform_type: 'browser view',
          form: {
            name: 'contact',
            step: '2',
            step_name: 'submit',
            type: 'lead',
            status: 'failure'
          }
        }
        if (typeof window.trackingNS?.track?.tracking === 'function') {
          window.trackingNS.track.tracking('pageview', trackData)
        }
      })
    },
    clearForm () {
      const comp = this
      this.vflowResetFeedbackStates()
      Object.entries(this.$refs).forEach(([key, comp]) => {
        if (/^nestedInput/.test(key)) {
          comp.vflowResetFeedbackStates()
        }
      })
      // Don't aks me why... we need to reset the feedback state first and
      // then clear all the input values
      Object.keys(this.formValues.customer).forEach((key) => {
        comp.formValues.customer[key] = ''
      })
      Object.keys(this.formValues.contact).forEach((key) => {
        comp.formValues.contact[key] = ''
      })
      this.$refs.nestedInputPhoneNumber.localMsisdn = ''
    },
    errorMessage (error) {
      const firstMessage = splitError(error)?.[0]
      if (firstMessage && firstMessage.extensions?.category === 'graphql') {
        // technical error message, do not use for customer
        return this.getSlotText('txtToastError', 'Es ist ein technischer Fehler aufgetreten.')
      }
      return firstMessage.message
    },
    customerIdFormatter () {
      return (v) => v.replace(/[^\d]/g, '').substring(0, 10)
    }
  },
  mixins: [
    validationMixin,
    isValid,
    validationFlow,
    formatterCharFilterOnlyAplha,
    formatDecimal,
    transaction('contactForm'),
    useFeature('showContactFormThirdOption'),
    beErrors,
    emailValidator
  ]
}
</script>
